import { Box, Chip, Tooltip, Typography } from "@mui/material"
import { AiMessageSteps } from "__generated__/graphql"
import { useMemo, useState } from "react"

import { ROLE_DECISION_LABELS, ROLE_LABELS } from "./aiStepsDrawer.utils"
import {
  buildUsedSources,
  ProfileSnapshot,
  PromptDecision,
  SourceItem,
  SourceKind,
  SourceUse
} from "./usedSources.utils"
import { ROLE_REASON_LABELS } from "./v2AgentLoop.labels"

const KIND_META: Record<
  SourceKind,
  { title: string; fg: string; border: string }
> = {
  guidance: { title: "Guidance", fg: "#2f6b3f", border: "#9cc3a6" },
  skill: { title: "Skill", fg: "#76528a", border: "#b9a4c3" },
  knowledge: { title: "Bilgi tabanı (RAG)", fg: "#866b2b", border: "#c8b77e" },
  action: { title: "Action", fg: "#9a5d35", border: "#cba38a" },
  mcp: { title: "Mağaza / MCP", fg: "#286477", border: "#78a9b5" }
}

const KIND_ORDER: SourceKind[] = [
  "guidance",
  "skill",
  "knowledge",
  "action",
  "mcp"
]

const TONE_LABELS: Record<string, string> = {
  Neutral: "Nötr",
  Friendly: "Samimi",
  Formal: "Resmi"
}

const LENGTH_LABELS: Record<string, string> = {
  CONCISE: "Kısa",
  STANDARD: "Standart",
  THOROUGH: "Detaylı"
}

/** Prompt flags that come straight from an AI profile setting, and which setting that is. */
const PROFILE_FLAGS: Record<string, string> = {
  emoji: "Emoji kullanımı",
  introduce: "Sohbet · kendini tanıtma",
  handoff_disabled: "Sohbet · insana devir kapalı",
  comment_public_reply: "Yorum · yoruma herkese açık cevap",
  comment_auto_dm: "Yorum · otomatik özel mesaj",
  alternatives_disabled: "Mağaza · benzer ürün önerme kapalı"
}

const FLAG_LABELS: Record<string, string> = {
  knowledge: "Bilgi tabanı açık",
  roles_unsettled: "Rol sabitlenmedi — ajan seçebilir",
  roles_switchable: "Rol değiştirilebilir",
  single_reply_channel: "Tek cevap hakkı olan yüzey",
  can_follow_up: "Takip mesajı atabilir",
  ticket: "Ticket yüzeyi",
  skills: "Skill'ler açık",
  skills_overflow: "Skill kataloğu kırpıldı",
  skill_active: "Devam eden skill var",
  session_summary: "Oturum özeti verildi",
  actions: "Kanal action'ları açık",
  spam: "Spam guidance'ı yazılmış",
  commerce: "Mağaza araçları açık",
  commerce_orders: "Sipariş işlemleri açık",
  marketplace_question: "Pazaryeri sorusu",
  marketplace_unresolved: "Pazaryeri ilanı eşleşmedi",
  published_answer_policy: "Yayınlanan cevap politikası",
  comment: "Yorum yüzeyi",
  private_reply: "Özel yanıt yüzeyi",
  post_context: "Gönderi bağlamı eklendi",
  subject_details: "Konu ürünün detayı eklendi",
  subject_hint: "Konu ürün ipucu eklendi",
  commerce_event: "Mağaza olayı (onay/iptal) var",
  pending_approval: "Onay bekleyen işlem var",
  media_notes: "Görsel/ses notları eklendi"
}

function StepRef({
  use,
  onJump
}: {
  use: SourceUse
  onJump?: (stepId: string) => void
}) {
  const label = `${use.stepNo !== null ? `#${use.stepNo} ` : ""}${use.where}`
  const chip = (
    <Chip
      size="small"
      variant="outlined"
      label={label}
      onClick={onJump ? () => onJump(use.stepId) : undefined}
      sx={{
        height: 20,
        fontSize: 10.5,
        maxWidth: 320,
        ...(use.failed && { color: "error.main", borderColor: "error.light" })
      }}
    />
  )
  return use.note ? (
    <Tooltip arrow title={use.note}>
      {chip}
    </Tooltip>
  ) : (
    chip
  )
}

function SectionHeader({
  title,
  count,
  color
}: {
  title: string
  count?: number
  color: string
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
      <Box
        aria-hidden="true"
        sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color }}
      />
      <Typography
        variant="caption"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.6,
          fontSize: 10.5,
          fontWeight: 700,
          color: "text.secondary"
        }}
      >
        {title}
        {count !== undefined ? ` · ${count}` : ""}
      </Typography>
    </Box>
  )
}

function Section({
  children,
  borderColor
}: {
  children: React.ReactNode
  borderColor: string
}) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        borderLeft: `3px solid ${borderColor}`
      }}
    >
      {children}
    </Box>
  )
}

function ItemRow({
  item,
  color,
  muted,
  onJump
}: {
  item: SourceItem
  color: string
  muted?: boolean
  onJump?: (stepId: string) => void
}) {
  const name = (
    <Typography
      variant="body2"
      fontWeight={600}
      sx={{ color: muted ? "text.disabled" : color, wordBreak: "break-word" }}
    >
      {item.name}
    </Typography>
  )
  return (
    <Box sx={{ py: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
        {item.text ? (
          <Tooltip
            arrow
            title={
              <Box
                sx={{
                  whiteSpace: "pre-wrap",
                  maxHeight: 320,
                  overflow: "auto"
                }}
              >
                {item.text}
              </Box>
            }
            slotProps={{
              popper: { sx: { "& .MuiTooltip-tooltip": { maxWidth: 520 } } }
            }}
          >
            {name}
          </Tooltip>
        ) : (
          name
        )}
        {item.detail && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {item.detail}
          </Typography>
        )}
        {item.uses.length > 1 && (
          <Typography
            variant="caption"
            sx={{ ml: "auto", color: "text.disabled", flexShrink: 0 }}
          >
            {item.uses.length} kez
          </Typography>
        )}
      </Box>
      {item.uses.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {item.uses.map((use) => (
            <StepRef key={use.stepId} use={use} onJump={onJump} />
          ))}
        </Box>
      )}
    </Box>
  )
}

function ProfileRows({ profile }: { profile: ProfileSnapshot }) {
  const rows: [string, string][] = [
    ["Rol", profile.role ? (ROLE_LABELS[profile.role] ?? profile.role) : "—"],
    ["Ajan adı", profile.name ?? "—"],
    [
      "Ses tonu",
      profile.toneOfVoice
        ? (TONE_LABELS[profile.toneOfVoice] ?? profile.toneOfVoice)
        : "—"
    ],
    [
      "Cevap uzunluğu",
      profile.answerLength
        ? (LENGTH_LABELS[profile.answerLength] ?? profile.answerLength)
        : "—"
    ],
    ["Emoji", profile.useEmoji ? "Açık" : "Kapalı"],
    [
      "İnsan cevaplarından öğrenme",
      profile.learnFromHumanReplies ? "Açık — bilgi aramasına dahil" : "Kapalı"
    ],
    [
      "Hazır cevaplar bilgi kaynağı",
      profile.useMacrosAsKnowledgeSource ? "Açık" : "Kapalı"
    ]
  ]
  if (profile.persona) rows.push(["Persona", profile.persona])
  if (profile.introduction) {
    rows.push(["Kendini tanıtma metni", profile.introduction])
  }
  if (profile.handoffDisabledMessage) {
    rows.push(["Devir kapalı mesajı", profile.handoffDisabledMessage])
  }

  return (
    <Box
      sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 12px" }}
    >
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ pt: 0.25 }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

function DecisionChips({
  flags,
  labels
}: {
  flags: PromptDecision[]
  labels: Record<string, string>
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {flags.map(({ flag, on }) => (
        <Tooltip key={flag} arrow title={flag}>
          <Chip
            size="small"
            label={`${on ? "✓" : "✕"} ${labels[flag] ?? flag}`}
            sx={{
              height: 20,
              fontSize: 10.5,
              fontWeight: 600,
              color: on ? "#1b5e20" : "text.disabled",
              bgcolor: on ? "rgba(46, 125, 50, 0.08)" : "rgba(0,0,0,0.04)"
            }}
          />
        </Tooltip>
      ))}
    </Box>
  )
}

/**
 * Every source the turn drew on, each with the steps it was used in. The chips jump to the raw
 * step, which is where someone goes once a line here raises a question.
 */
export default function UsedSourcesView({
  steps,
  onJumpToStep
}: {
  steps: AiMessageSteps[] | undefined
  onJumpToStep?: (stepId: string) => void
}) {
  const sources = useMemo(() => buildUsedSources(steps), [steps])
  const [showOff, setShowOff] = useState(false)

  const flags = sources.decisions?.flags ?? []
  const profileFlags = flags.filter(({ flag }) => PROFILE_FLAGS[flag])
  const turnFlags = flags.filter(({ flag }) => !PROFILE_FLAGS[flag])
  const turnOn = turnFlags.filter(({ on }) => on)
  const turnOff = turnFlags.filter(({ on }) => !on)
  const unused = KIND_ORDER.filter(
    (kind) =>
      sources.groups[kind].length === 0 &&
      !(kind === "guidance" && sources.guidanceCountOnly)
  )
  const role = sources.role
  const roleReason = role
    ? (ROLE_REASON_LABELS[role.reason] ??
      ROLE_DECISION_LABELS[role.reason] ??
      role.reason)
    : ""

  return (
    <Box>
      <Section borderColor="#afbbc2">
        <SectionHeader title="AI profili" color="#afbbc2" />
        {role && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1,
              flexWrap: "wrap"
            }}
          >
            <Typography variant="body2">
              <b>{ROLE_LABELS[role.role] ?? role.role}</b> rolünün profili
              kullanıldı
            </Typography>
            {roleReason && (
              <Typography variant="caption" color="text.secondary">
                · {roleReason}
              </Typography>
            )}
            <StepRef
              use={{
                stepId: role.stepId,
                stepNo: role.stepNo,
                where: "Rol kararı",
                note: "",
                failed: false
              }}
              onJump={onJumpToStep}
            />
          </Box>
        )}
        {sources.profile ? (
          <>
            <ProfileRows profile={sources.profile} />
            {profileFlags.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Profil ayarlarından prompta geçen kararlar
                </Typography>
                <DecisionChips flags={profileFlags} labels={PROFILE_FLAGS} />
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <StepRef
                use={{
                  stepId: sources.profile.stepId,
                  stepNo: sources.profile.stepNo,
                  where: "Ajan promptu",
                  note: "",
                  failed: false
                }}
                onJump={onJumpToStep}
              />
            </Box>
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Bu run hangi profil alanlarının prompta girdiğini kaydetmemiş (v1
            motoru ya da kayıt eklenmeden önceki bir v2 run&apos;ı).
          </Typography>
        )}
      </Section>

      {sources.decisions && (
        <Section borderColor="#afbbc2">
          <SectionHeader
            title="Prompt kararları"
            count={turnOn.length}
            color="#afbbc2"
          />
          <DecisionChips flags={turnOn} labels={FLAG_LABELS} />
          {turnOff.length > 0 && (
            <Box sx={{ mt: 0.75 }}>
              <Typography
                variant="caption"
                role="button"
                tabIndex={0}
                onClick={() => setShowOff((prev) => !prev)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") setShowOff((prev) => !prev)
                }}
                sx={{
                  color: "text.secondary",
                  cursor: "pointer",
                  textDecoration: "underline"
                }}
              >
                {showOff
                  ? "Kapalı kararları gizle"
                  : `Kapalı kararları göster (${turnOff.length})`}
              </Typography>
              {showOff && (
                <Box sx={{ mt: 0.5 }}>
                  <DecisionChips flags={turnOff} labels={FLAG_LABELS} />
                </Box>
              )}
            </Box>
          )}
        </Section>
      )}

      {KIND_ORDER.map((kind) => {
        const meta = KIND_META[kind]
        const items = sources.groups[kind]
        const dropped = kind === "guidance" ? sources.droppedGuidance : []
        const countOnly = kind === "guidance" ? sources.guidanceCountOnly : null
        if (items.length === 0 && dropped.length === 0 && !countOnly)
          return null

        return (
          <Section key={kind} borderColor={meta.border}>
            <SectionHeader
              title={meta.title}
              count={items.length}
              color={meta.border}
            />
            {items.map((item) => (
              <ItemRow
                key={item.key}
                item={item}
                color={meta.fg}
                onJump={onJumpToStep}
              />
            ))}
            {countOnly !== null && items.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                {countOnly} guidance satırı ajana verildi — bu run hangileri
                olduğunu kaydetmemiş.
              </Typography>
            )}
            {dropped.length > 0 && (
              <Box sx={{ mt: 0.75 }}>
                <Typography variant="caption" color="text.secondary">
                  Prompt bütçesine sığmadığı için elenen · {dropped.length}
                </Typography>
                {dropped.map((item) => (
                  <ItemRow
                    key={item.key}
                    item={{ ...item, uses: [] }}
                    color={meta.fg}
                    muted
                  />
                ))}
              </Box>
            )}
          </Section>
        )
      })}

      {unused.length > 0 && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.disabled">
            Bu turda kullanılmayan:{" "}
            {unused.map((kind) => KIND_META[kind].title).join(", ")}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
